import {
  ActivityInboxType,
  EnvConfig,
  ISSUE_MESSAGE_PAGE_COUNT,
  Issue,
  MediaType,
  Message,
  useFindIssueMessages,
  useNewIssueMessageSubscription,
  useUnreadActivityInboxSubscription,
} from '@gripp/shared-logic';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { FlatList, StyleSheet, View } from 'react-native';
import { ActivityIndicator, Text } from 'react-native-paper';
import { Layout } from '../../styles';
import { Colors } from '../../themes';
import {
  ImageGalleryImage,
  ImageGalleryModal,
} from '../crop-circle/Components/ImageGallery';
import { DocumentBIcon } from '../crop-circle/icons';
import { spacing, tokens } from '../crop-circle/tokens';
import { BottomComponentBar } from '../navigation';
import { IssueMessage } from './issueMessage';

export type IssueMessagesProps = {
  issue: Issue;
  onMessagesCountChange?: (messagesCount: number) => void;
  mediaUrl: string;
  inbox: any[];
  tsUpdate?: string;
  setAsRead: (
    issueId: string,
    inboxes: any[],
    invalidate: boolean
  ) => Promise<void>;
  /** Forwarded to each message's overflow "Call" action (gated upstream). */
  onCall?: (actor: { id: string; name: string }) => void;
};

export type IssueMessagesRef = {
  addNewMessage: (message: Message) => void;
};

export const IssueMessages = forwardRef<IssueMessagesRef, IssueMessagesProps>(
  (props, ref) => {
    const [messages, setMessages] = useState<Message[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const {
      data,
      isLoading,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
      refetch,
    } = useFindIssueMessages(props.issue.id);
    // if a user goes back and then revisits this page, data might be cached and we could already have more than 1 page
    const [page, setPage] = useState(data?.pages?.length || 0);
    const initialPages = data?.pages?.length || 0;

    const [galleryVisible, setGalleryVisible] = useState(false);
    const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);

    const allGalleryImages = useMemo<ImageGalleryImage[]>(() => {
      return messages.flatMap((message) =>
        (message.media ?? [])
          .filter((m) => m.type === MediaType.Image)
          .map((m) => ({
            id: m.id,
            url: `${EnvConfig.mediaUrl}/images/${m.id}/original.${m.extension}`,
            name: m.name,
          }))
      );
    }, [messages]);

    const handleImagePress = (mediaId: string) => {
      const idx = allGalleryImages.findIndex((img) => img.id === mediaId);
      if (idx >= 0) {
        setGalleryInitialIndex(idx);
        setGalleryVisible(true);
      }
    };

    useNewIssueMessageSubscription(props.issue.id, (response) => {
      addNewMessage({ ...response.data.newIssueMessage, isNew: true });
      return Promise.resolve();
    });

    useUnreadActivityInboxSubscription(async (inboxItem) => {
      if (inboxItem.data.id === props.issue.id) {
        await props.setAsRead(props.issue.id, [inboxItem], false);
      }
    });

    const addNewMessage = (message: Message) => {
      setMessages((prevMessages: Message[]) => [message, ...prevMessages]);
    };

    useImperativeHandle(ref, () => ({
      addNewMessage: addNewMessage,
    }));

    useEffect(() => {
      if (!props.tsUpdate) return;

      refetch();
    }, [props.tsUpdate, refetch]);

    useEffect(() => {
      if (
        isLoading ||
        isFetchingNextPage ||
        !data ||
        !data.pages ||
        data.pages.length === 0
      )
        return;

      if (page > 0 && page > initialPages) {
        setMessages((prevMessages) => [
          ...prevMessages,
          ...data.pages[page].items,
        ]);
      } else {
        setTotalCount(data.pages[0].count);
        props.onMessagesCountChange &&
          props.onMessagesCountChange(data.pages[0].count);
        setMessages(data.pages.flatMap((page) => page?.items));
      }
    }, [data, initialPages, isFetchingNextPage, isLoading, page]);

    const onEndReached = () => {
      if (!totalCount || totalCount <= ISSUE_MESSAGE_PAGE_COUNT) return;
      if (!isFetchingNextPage && hasNextPage) {
        setPage(page + 1);
        fetchNextPage();
      }
    };

    const isUnread = (message: any) => {
      if (!props.inbox) {
        return false;
      }

      return (
        message.isNew ||
        props.inbox.some(
          (i) =>
            i.type === ActivityInboxType.IssueMessageCreated &&
            i.data.messageId === message.id
        )
      );
    };
    return (
      <View style={[styles.container]}>
        {isFetchingNextPage && (
          <View>
            <ActivityIndicator
              size="large"
              animating={isLoading || isFetchingNextPage}
            />
          </View>
        )}
        <FlatList
          refreshing={isFetchingNextPage}
          data={messages}
          extraData={messages}
          inverted={true}
          onEndReached={onEndReached}
          renderItem={({ item, index }) => (
            <IssueMessage
              key={item.id}
              message={item}
              mediaUrl={props.mediaUrl}
              isUnread={isUnread(item)}
              issue={props.issue}
              isFirstMessage={index === messages.length - 1}
              onImagePress={handleImagePress}
              onCall={props.onCall}
            />
          )}
        />

        {allGalleryImages.length > 0 && (
          <ImageGalleryModal
            visible={galleryVisible}
            onClose={() => setGalleryVisible(false)}
            images={allGalleryImages}
            initialIndex={galleryInitialIndex}
            renderHeader={(activeImage) => (
              <BottomComponentBar
                left={
                  <View style={styles.leftNode}>
                    <DocumentBIcon />
                    <Text
                      style={tokens.typography.body.mediumEmphasised}
                      numberOfLines={1}
                    >
                      {activeImage?.name && activeImage.name.length > 25
                        ? `${activeImage.name.slice(0, 25)}...`
                        : activeImage?.name}
                    </Text>
                  </View>
                }
                backButton={undefined}
              />
            )}
            contentStyle={{ maxHeight: 500 }}
          />
        )}
      </View>
    );
  }
);

const styles = StyleSheet.create({
  container: {
    ...Layout.container,
    backgroundColor: Colors.brand.white,
  },
  leftNode: {
    marginLeft: spacing.md,
    marginTop: spacing.md,
    marginRight: 'auto',
    display: 'flex',
    flexDirection: 'row',
    gap: 4,
    alignItems: 'center',
  },
});
